import React, { useRef } from 'react';
import { useAppContext } from '@/Main';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor, rgbaColor } from '@/helpers/utils';
import PropTypes from 'prop-types';
import { Card, Form } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import PlotSkeleton from './PlotSkeleton';
import timeStampToDate from '@/helpers/timeStampToDate';
import numberHumanizer from '@/helpers/numberHumanizer';

echarts.use([LineChart, TooltipComponent, GridComponent, LegendComponent]);

const RANGE_OPTIONS = [
  { key: 'month', label: '۱ ماه', points: 1 },
  { key: 'quarter', label: '۳ ماه', points: 3 },
  { key: 'halfYear', label: '۶ ماه', points: 6 },
  { key: 'year', label: '۱ سال', points: 12 },
  { key: 'all', label: 'همه', points: Infinity }
];

type AuthoritySeries = {
  data?: unknown[];
  [key: string]: unknown;
};

type AuthorityData = {
  data?: unknown[];
  series?: AuthoritySeries[];
  [key: string]: unknown;
};

const sliceAuthorityData = (
  data: AuthorityData | null | undefined,
  rangeKey: string
) => {
  if (!data) return data;

  const option = RANGE_OPTIONS.find(item => item.key === rangeKey);
  const points = option?.points ?? Infinity;
  const categories = data.data ?? [];
  if (!categories.length || points === Infinity || points >= categories.length) {
    return data;
  }

  const start = categories.length - points;
  return {
    ...data,
    data: categories.slice(start),
    series: data.series?.map(series => ({
      ...series,
      data: Array.isArray(series.data) ? series.data.slice(start) : series.data
    }))
  };
};

/** Keep the visible axis readable while preserving every value in the tooltip. */
const axisLabelInterval = (
  categoryCount: number,
  maxLabels: number = 16
): number => {
  if (categoryCount <= maxLabels) return 0;
  return Math.ceil(categoryCount / maxLabels) - 1;
};

const getOption = (
  data: AuthorityData | null | undefined,
  isDark: boolean
) => {
  const categories = data?.data ?? [];
  const labelMax = typeof window !== 'undefined' && window.innerWidth < 768 ? 10 : 16;
  const rotateLabels = categories.length > 10 ? 40 : 0;

  return {
  textStyle: {
    fontFamily: 'dana'
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    },
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    borderWidth: 1,
    transitionDuration: 0,
    textStyle: {
      fontWeight: 500,
      fontSize: 12,
      color: getColor('gray-1100')
    },
    formatter: (
      params: Array<{
        marker?: string;
        name?: string | number;
        value?: string | number;
      }>
    ) => {
      return `<div>
         <span>${params[0].marker}</span>
           <span class="fw-bolder">
           ${timeStampToDate(params[0].name).slice(8).slice(0, -3)}:
           </span>
           <span>${numberHumanizer(params[0].value)}</span>
           </div>`;
    }
  },
  xAxis: {
    type: 'category',
    data: data?.data,
    boundaryGap: false,
    splitLine: {
      show: false,
      lineStyle: {
        color: rgbaColor(getColor('primary'), 0.1)
      },
      interval: 0
    },
    axisLine: {
      lineStyle: {
        color: rgbaColor(getColor('primary'), 0.1)
      }
    },
    axisTick: {
      show: false,
      length: 10,
      lineStyle: {
        color: rgbaColor(getColor('primary'), 0.1)
      }
    },
    axisPointer: {
      lineStyle: {
        color: getColor('gray-300')
      }
    },
    axisLabel: {
      color: getColor('gray-400'),
      fontWeight: 600,
      fontSize: 11,
      rotate: rotateLabels,
      margin: rotateLabels ? 12 : 8,
      hideOverlap: true,
      interval: axisLabelInterval(categories.length, labelMax),
      formatter: (value: string | number) =>
        timeStampToDate(value).slice(8).slice(0, -3)
    }
  },
  yAxis: {
    type: 'value',

    splitLine: {
      show: true,
      lineStyle: {
        color: rgbaColor(getColor('primary'), 0.1)
      },
      interval: 0
    },
    axisLabel: {
      show: true
    },
    axisTick: {
      show: true,
      length: 10,
      lineStyle: {
        color: rgbaColor(getColor('primary'), 0.1)
      }
    },
    axisLine: { show: true }
  },
  series: data?.series?.map(x => ({
    type: 'line',
    smooth: false,
    data: x.data,
    symbol: 'emptyCircle',
    itemStyle: {
      color: isDark ? getColor('primary') : getColor('primary')
    },
    lineStyle: {
      color: isDark
        ? rgbaColor(getColor('primary'), 0.8)
        : rgbaColor(getColor('primary'), 0.8)
    },

    emphasis: {
      lineStyle: {
        width: 2
      }
    }
  })),
  grid: {
    right: 25,
    left: 55,
    bottom: rotateLabels ? '25%' : '12%',
    top: 0
  }
  };
};

const DomainAuthorityChart = ({
  data,
  style,
  loading = false
}: Record<string, any>) => {
  const {
    config: { isDark }
  } = useAppContext();
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [range, setRange] = React.useState('all');
  const visibleData = sliceAuthorityData(data, range);
  return (
    <Card ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            دامین اتوریتی Moz سایت شما
            <HelpTooltip html="امتیاز دامین اتوریتی Moz بین ۰ تا ۱۰۰ است و اعتبار کلی دامنهٔ ارجاع‌دهنده را نشان می‌دهد؛ هرچه امتیاز بیشتر باشد، اعتبار دامنه بیشتر است. نمودار روند امتیاز را در طول زمان نمایش می‌دهد." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={range}
              onChange={event => setRange(event.target.value)}
              aria-label="بازه دامین اتوریتی Moz"
              style={{ width: 'auto' }}
            >
              {RANGE_OPTIONS.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <CardChartDropDown cardRef={cardRef} chartRef={chartRef} />
          </div>
        }
      />
      <Card.Body>
        {loading ? (
          <PlotSkeleton height={style?.height ?? '300px'} />
        ) : (
          <ReactEChartsCore
            echarts={echarts}
            ref={chartRef}
            option={getOption(visibleData, isDark)}
            style={style}
            notMerge
          />
        )}
      </Card.Body>
    </Card>
  );
};

DomainAuthorityChart.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.array,
    series: PropTypes.arrayOf(
      PropTypes.shape({
        data: PropTypes.array
      })
    )
  }),
  style: PropTypes.object,
  loading: PropTypes.bool
};

export default DomainAuthorityChart;
